import { FC, useState } from 'react'
import { NavLink } from 'react-router'
import { Autocomplete, Box, MenuItem, TextField } from '@mui/material'
import { useDebounceValue } from 'usehooks-ts'

import { RegionResponse } from '@/modules/api/openapi/models/RegionResponse'
import { DEBOUNCE_SEARCH_DELAY } from '@/modules/curbRegulations/constants'
import { useCurrentRegion, useRegions } from '@/modules/urlRouting/hooks'
import { SELECTOR_BOX_SX } from '@/modules/urlRouting/styles'
import { usePathWithNewRegionId } from '@/modules/urlRouting/utils'

// Big enough that the first page always overflows the list box, so the user can scroll to load
// the next page. With a small page, a tall screen shows every option with no scroll bar.
const REGIONS_PER_PAGE = 50

const RegionSelectorMUI: FC = () => {
  const [inputValue, setInputValue] = useState('')

  const [debouncedInputValue] = useDebounceValue(inputValue, DEBOUNCE_SEARCH_DELAY)

  const { data: activeRegion } = useCurrentRegion<RegionResponse>()
  const generatePathWithNewRegionId = usePathWithNewRegionId()

  const {
    data: filteredRegions,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useRegions({
    size: REGIONS_PER_PAGE,
    ...(inputValue && { search: debouncedInputValue }),
  })

  const regionOptions = filteredRegions?.pages.flatMap(page => page.items) ?? []

  return (
    <Box sx={SELECTOR_BOX_SX} data-testid="region-selector">
      <Autocomplete
        size="small"
        freeSolo
        filterOptions={x => x}
        value={activeRegion}
        options={regionOptions}
        getOptionLabel={option => (typeof option === 'string' ? '' : option.text)}
        sx={{ width: '100%' }}
        renderOption={(props, option, state, ownerState) => (
          <MenuItem
            key={option.text}
            component={NavLink}
            to={generatePathWithNewRegionId(option.regionId)}
            data-testid="region-selector-option"
            sx={theme => ({ '&:hover': { color: theme.palette.primary.main } })}
          >
            {option.text}
          </MenuItem>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label="Region"
            onChange={event => setInputValue(event.target.value)}
          />
        )}
        slotProps={{
          listbox: {
            onScroll: (event: React.SyntheticEvent) => {
              const { scrollTop, clientHeight, scrollHeight } = event.currentTarget
              const nearBottom = scrollTop + clientHeight > scrollHeight - 10
              if (nearBottom && hasNextPage && !isFetchingNextPage) fetchNextPage()
            },
          },
        }}
      />
    </Box>
  )
}

export default RegionSelectorMUI
